import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { isShortcutHelpEvent } from "../keyboard-shortcuts";
import {
  isEditableKeyboardTarget,
  shortcutLabel,
} from "../markdown/document-session";
import {
  visibleHeaderQuickActions,
  workspaceComposePath,
} from "../workspace-chrome";
import { HeaderIcon } from "./HeaderIcon";
import KeyboardShortcutsDialog from "./KeyboardShortcutsDialog";

export function HeaderQuickActions({
  organizationId,
  canAdminister,
}: {
  readonly organizationId: string;
  readonly canAdminister: boolean;
}) {
  const navigate = useNavigate();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const actions = visibleHeaderQuickActions(canAdminister);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isShortcutHelpEvent(event)) {
        return;
      }
      if (isEditableKeyboardTarget(event.target)) {
        return;
      }
      event.preventDefault();
      setShortcutsOpen((current) => !current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        rootRef.current?.contains(event.target) !== true
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="header-quick-actions">
      <div className="header-quick-actions__compose" ref={rootRef}>
        <button
          className="header-icon-button"
          type="button"
          aria-expanded={open}
          aria-controls={menuId}
          aria-haspopup="menu"
          aria-label="Add"
          title="Add"
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          <HeaderIcon name="add" />
        </button>
        {open ? (
          <div className="header-quick-actions__menu" id={menuId} role="menu">
            {actions.map((item) => (
              <button
                key={item.action}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void navigate(
                    workspaceComposePath(organizationId, item.action),
                  );
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <Link
        className="header-icon-button"
        to={`/org/${organizationId}/search`}
        aria-label="Search"
        title="Search"
      >
        <HeaderIcon name="search" />
      </Link>
      <button
        className="header-icon-button"
        type="button"
        aria-label="Keyboard shortcuts"
        title={`Keyboard shortcuts (${shortcutLabel("/")})`}
        onClick={() => {
          setShortcutsOpen(true);
        }}
      >
        <HeaderIcon name="shortcuts" />
      </button>
      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />
    </div>
  );
}
